// Animated 8-direction walk for every character (prototype 2026-09-15, on by default 2026-09-18).
// The sheets come from scripts/anim/build_walk_sheets.py (ChatGPT-generated frames): one
// sheet for the body and one per garment, 8 frames x 5 rows (side, front, back,
// front34, back34) plus a standing row. Right-facing directions are the left-facing rows flipped.
// The game loads them packed (scripts/anim/pack_walk_sheet.py, anim/packed/): each frame cut to its
// pixels in one small atlas, ~10% of the grid's memory, named by the same frame numbers and drawn
// where its 320x480 cell put it.
import Phaser from 'phaser';
import { assetUrl } from '../assetVersion.js';
import { hairFallback, loadHair } from '../look/hairArt.mjs';

// On for everyone since 2026-09-18. `?anim=0` is the kill switch (remembered, like ?dpr); `?anim=1` clears it.
export const ANIM_ENABLED = (() => {
  try {
    const q = new URLSearchParams(window.location.search).get('anim');
    if (q != null) window.localStorage.setItem('anim', q);
    return (q ?? window.localStorage.getItem('anim')) !== '0';
  } catch { return true; }
})();

export const WALK = {
  frameW: 320, frameH: 480, cols: 8,
  // The figure is 400 of the cell's 480 px (64 px of headroom for hats), so the cell is drawn 480/400 as
  // tall as the flat art's box to keep her the same size; `dy` puts her feet back where the 440-px
  // sheet had them (row 464 of a 240-px box = 112 px below the centre).
  display: (w, h) => ({ w: w * 480 / 400, h: h * 480 / 400, dy: -(h * 480 / 400 * (464 / 480 - 0.5) - h * (464 / 480 - 0.5)) }),
  rows: ['side', 'front', 'back', 'front34', 'back34'],
  // The front/back sheets alternate legs every frame (a 2-pose cycle in effect), so they
  // read too fast at the side view's rate.
  fps: [10, 6, 6, 9, 9],
  standRow: 5,   // row 5 holds one standing pose per direction (columns in `rows` order)
  // The clip's frame 0 is the passing pose (feet together); starting there reads as a slow
  // first step every time she sets off, so the loop starts on the full-stride frame.
  startFrame: 2,
  // the hairless body (character look plan 2); the hair is its own pair of sheets (look/hairArt.mjs). `<c>_walk`,
  // the body with today's hair, stays only as the garment builder's diff base.
  body: (character) => `${character}_walk_body`,
  // one sheet per garment per character, the same grid, built by scripts/anim/build_walk_sheets.py
  garment: (character, itemId) => `${character}_walk_${itemId}`,
};

// Screen-space move vector (y down) → which sheet row to play and whether to mirror it.
// The generated art faces the viewer's LEFT: side = W, front34 = SW, back34 = NW.
export function dirFromVector(dx, dy) {
  const a = Math.atan2(dy, dx) * 180 / Math.PI;      // E = 0, S = 90, W = ±180, N = -90
  if (a > -22.5 && a <= 22.5) return { row: 0, flip: true };     // E
  if (a > 22.5 && a <= 67.5) return { row: 3, flip: true };      // SE
  if (a > 67.5 && a <= 112.5) return { row: 1, flip: false };    // S (towards the viewer)
  if (a > 112.5 && a <= 157.5) return { row: 3, flip: false };   // SW
  if (a > 157.5 || a <= -157.5) return { row: 0, flip: false };  // W
  if (a > -157.5 && a <= -112.5) return { row: 4, flip: false }; // NW
  if (a > -112.5 && a <= -67.5) return { row: 2, flip: false };  // N (away)
  return { row: 4, flip: true };                                  // NE
}

// 8-way compass index: E=0, SE=1, S=2, SW=3, W=4, NW=5, N=6, NE=7 (what goes over the wire and into the DB);
// it lives in dir8.mjs so the ride's pure modules share it without Phaser.
export { dir8FromVector, rowFlipFromDir8 } from './dir8.mjs';

export function animKey(sheetKey, row) { return `${sheetKey}:walk:${WALK.rows[row]}`; }

// The frame to hold while standing: the direction's standing pose if the sheet has that row,
// otherwise the first walk frame of the direction.
export function restFrame(scene, sheetKey, row) {
  const standIndex = WALK.standRow * WALK.cols + row;
  return scene.textures.get(sheetKey).has(standIndex) ? standIndex : row * WALK.cols;
}

function createAnims(scene, sheetKey) {
  WALK.rows.forEach((_, row) => {
    const key = animKey(sheetKey, row);
    if (scene.anims.exists(key)) return;
    scene.anims.create({
      key,
      frames: scene.anims.generateFrameNumbers(sheetKey, { start: row * WALK.cols, end: row * WALK.cols + WALK.cols - 1 }),
      frameRate: WALK.fps[row], repeat: -1,
    });
  });
}

// Load the sheets this scene does not have yet (the manifest never lists them), then
// build the animations and call back. Safe to call while the scene's loader is busy.
export function ensureWalkSheets(scene, keys, cb) { loadPacked(scene, keys, cb, (k) => createAnims(scene, k)); }

// A hairstyle's two sheets ({front, back}); when either does not load, the default style's instead (never a bald
// head). cb gets the keys it ended up with, or null when not even the default's loaded (look/hairArt.mjs loadHair).
export function ensureHairSheets(scene, keys, character, cb) {
  loadHair((k, done) => ensureWalkSheets(scene, k, done), (k) => scene.textures.exists(k), keys, hairFallback(character), cb);
}

// Drop hair sheets nobody draws (look/hairSheets.mjs), with their walk animations: a sprite that wants one again
// loads it again through ensureWalkSheets, which builds the animations anew.
export function freeHairSheets(scene, keys) {
  for (const k of keys) {
    WALK.rows.forEach((_, row) => scene.anims.remove(animKey(k, row)));
    scene.textures.remove(k);
  }
}

// The sofa / bed pose atlases (poseLayers.mjs) come from the same folder; they hold still frames, no animations.
export function ensurePoseSheets(scene, keys, cb) { loadPacked(scene, keys, cb); }

function loadPacked(scene, keys, cb, onLoaded = () => {}) {
  const missing = keys.filter((k) => !scene.textures.exists(k));
  const done = () => { keys.filter((k) => scene.textures.exists(k)).forEach(onLoaded); cb(); };
  if (!missing.length) { done(); return; }
  const pending = new Set(missing);
  const settle = (k) => {
    if (!pending.delete(k)) return;
    if (pending.size === 0) { scene.load.off(Phaser.Loader.Events.FILE_LOAD_ERROR, onError); done(); }
  };
  // One shared error listener: `once` per key would be consumed by whichever file fails first.
  const onError = (file) => { if (pending.has(file.key)) { console.warn('[walkAnim] missing sheet', file.key); scene.__packedInFlight?.delete(file.key); settle(file.key); } };
  scene.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, onError);
  // A key another caller already queued is only waited for: queuing it twice makes Phaser refuse the second copy
  // ("Texture key already in use") — a room preloads the sofa pose while sitting down asks for it too.
  const inFlight = (scene.__packedInFlight ??= new Set());
  for (const k of missing) {
    scene.load.once(`${Phaser.Loader.Events.FILE_KEY_COMPLETE}atlasjson-${k}`, () => { inFlight.delete(k); settle(k); });
    if (inFlight.has(k)) continue;
    inFlight.add(k);
    scene.load.atlas(k, assetUrl(`/assets/anim/packed/${k}.webp`), assetUrl(`/assets/anim/packed/${k}.json`));
  }
  // A file that downloaded but failed to decode (the dev server answers a missing file with
  // HTML) reports through a different path; when the loader is done, whatever is still
  // pending is missing for good.
  scene.load.once(Phaser.Loader.Events.COMPLETE, () => { for (const k of [...pending]) { scene.__packedInFlight?.delete(k); settle(k); } });
  if (!scene.load.isLoading()) scene.load.start();
}
